import { handleQuery } from '../utils/request'

const AI_AGENTS_API_URL = `${process.env.API_URL}/ai-agents/graphql`

// Minimal sequential queue for previewInsight only
let previewInsightRequestChain = Promise.resolve();
let previewInsightQueueCancelled = false;
function enqueuePreviewInsightRequest(run) {
  const p = previewInsightRequestChain.then(() => {
    if (previewInsightQueueCancelled) {
      // Stop executing further queued tasks
      return Promise.reject(new Error('PREVIEW_INSIGHT_QUEUE_CANCELLED'));
    }
    return run();
  });
  // Keep the chain alive even if a request fails
  previewInsightRequestChain = p.catch(() => { });
  return p;
}

// Abort controllers for insight preview requests
let currentPreviewInsightController = null;
let previewInsightControllers = new Set();

export function cancelPreviewInsightRequests() {
  try { currentPreviewInsightController && currentPreviewInsightController.abort(); } catch (e) { }
  currentPreviewInsightController = null;
  try {
    for (const c of previewInsightControllers) { try { c.abort(); } catch (_) { } }
  } finally {
    previewInsightControllers.clear();
  }
  previewInsightQueueCancelled = true;
  previewInsightRequestChain = Promise.resolve();
}

const AI_CONVERSATION_RETURN_VALUES = {
  _id: true,
  name: true,
  model: true,
  contextType: true,
  demoAppInput: true,
  createdAt: true,
  updatedAt: true,
  isResolved: true,
  lastMessageAt: true,
  user: {
    _id: true,
    firstName: true,
    lastName: true
  }
}

const AI_AGENT_RETURN_VALUES = {
  conversationId: true,
  message: {
    reasoning: true,
    suggestions: true,
    text: true,
    introduction: true,
    summary: true,
    moreToReview: true,
    layout: true,
    question: true,
    options: true,
    demoApp: true,
    response: {
      type: true,
      visoChart: true,
      content: true,
      table: {
        title: true,
        data: true,
        definitions: {
          column: true,
          label: true
        }
      },
      chart: {
        chartType: true,
        title: true,
        data: true,
        xKey: true,
        yKey: true,
        stackKeys: true,
        nameKey: true,
        dataKey: true
      },
      links: {
        title: true,
        url: true
      },
      fix: true,
      impact: true,
      reason: true,
      chartTitle: true,
      removeChart: true,
      placement: true,
      purpose: true
    }
  }
}

const AI_MESSAGE_RETURN_VALUES = {
  _id: true,
  senderRole: true,
  content: AI_AGENT_RETURN_VALUES.message,
  createdAt: true,
  updatedAt: true,
  outputType: true,
  sender: {
    _id: true,
    firstName: true,
    lastName: true
  }
}

const AGENT_SETTINGS_RETURN_VALUES = {
  id: true,
  workspaceId: true,
  userProfileId: true,
  contextType: true,
  createdAt: true,
  updatedAt: true,
  dataContext: {
    timeWindow: {
      type: true,
      value: true,
      start: true,
      end: true,
    },
    devices: true
  },
  responseStyle: {
    detailLevel: true,
    tone: true,
    formatting: {
      unit: true,
      currency: true,
      decimals: true
    }
  },
  locations: {
    id: true,
    name: true
  }
}

const AI_INSIGHTS_RETURN_VALUES = {
  id: true,
  workspaceId: true,
  dataSource: true,
  title: true,
  dashboardId: true,
  chartsToInclude: true,
  mode: true,
  text: true,
  status: true,
  createdBy: true,
  createdAt: true,
  updatedAt: true,
  keyFinding: {
    enabled: true,
    instructions: true,
    include: true,
    textSize: true,
    highlightKeyItems: true
  },
  briefing: {
    enabled: true,
    title: true,
    highlightKeyItems: true,
    summary: {
      enabled: true,
      subtitle: true,
      instructions: true,
      include: true,
      format: true
    },
    changeDrivers: {
      enabled: true,
      subtitle: true,
      instructions: true,
      include: true,
      focusOn: true,
      format: true,
      changeSensitivity: true
    },
    anomalyRadar: {
      enabled: true,
      subtitle: true,
      instructions: true,
      include: true,
      exclude: true,
      format: true,
      changeSensitivity: true
    },
    patternFinder: {
      enabled: true,
      subtitle: true,
      instructions: true,
      include: true,
      format: true,
      changeSensitivity: true
    },
    timelineOfEvents: {
      enabled: true,
      subtitle: true,
      instructions: true,
      include: true,
      format: true
    },
    proposeActions: {
      enabled: true,
      subtitle: true,
      instructions: true,
      actionType: true,
      format: true
    }
  }
}

export function getAiInsights(args) {
  const query = {
    query: {
      aiInsights: {
        __args: args,
        data: AI_INSIGHTS_RETURN_VALUES
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function generateInsightData(args) {
  const query = {
    mutation: {
      generateInsightData: {
        __args: args,
        insightId: true,
        status: true
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

const DEMO_APPS_RETURN_VALUES = {
  id: true,
  workspaceId: true,
  name: true,
  conversationId: true,
  createdBy: true,
  createdAt: true,
  updatedAt: true,
}

const DEMO_APP_RETURN_VALUES = {
  id: true,
  workspaceId: true,
  demoPitch: true,
  name: true,
  metrics: true,
  conversationId: true,
  createdBy: true,
  createdAt: true,
  updatedAt: true,
  charts: true,
  rawData: true
}

export function getDemoApps(args) {
  const query = {
    query: {
      demoApps: {
        __args: args,
        data: DEMO_APPS_RETURN_VALUES
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function getDemoApp(args) {
  const query = {
    query: {
      demoApp: {
        __args: args,
        ...DEMO_APP_RETURN_VALUES
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function deleteDemoApp(args) {
  const query = {
    mutation: {
      deleteDemoApp: {
        __args: args
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function getAiSuggestions(args) {
  const query = {
    query: {
      suggestions: {
        __args: args
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function getAiConversations(args) {
  const query = {
    query: {
      conversations: {
        __args: args,
        count: true,
        data: AI_CONVERSATION_RETURN_VALUES
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function getAgentSettings(args) {
  const query = {
    query: {
      agentSettings: {
        __args: args,
        ...AGENT_SETTINGS_RETURN_VALUES
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function getAiMessages(args) {
  const query = {
    query: {
      messages: {
        __args: args,
        count: true,
        data: AI_MESSAGE_RETURN_VALUES
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function askAgent(args) {
  const query = {
    mutation: {
      askAgent: {
        __args: args,
        ...AI_AGENT_RETURN_VALUES
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function deleteMessage(args) {
  const query = {
    mutation: {
      deleteMessage: {
        __args: args
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function deleteConversation(args) {
  const query = {
    mutation: {
      deleteConversation: {
        __args: args
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function updateAgentSettings(args) {
  const query = {
    mutation: {
      updateAgentSettings: {
        __args: args,
        ...AGENT_SETTINGS_RETURN_VALUES
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function createAgentSettings(args) {
  const query = {
    mutation: {
      createAgentSettings: {
        __args: args,
        ...AGENT_SETTINGS_RETURN_VALUES
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function getModels() {
  const query = {
    query: {
      models: {
        name: true,
        type: true
      }
    }
  }
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function createAiInsight(args) {
  const query = {
    mutation: {
      createAiInsight: {
        __args: args,
        id: true,
        workspaceId: true,
        dataSource: true,
        title: true,
        dashboardId: true,
        chartsToInclude: true,
        mode: true,
        text: true,
        createdBy: true,
        createdAt: true,
        updatedAt: true,
        status: true,
        keyFinding: {
          enabled: true,
          instructions: true,
          include: true,
          textSize: true,
          highlightKeyItems: true
        },
        briefing: {
          enabled: true,
          title: true,
          highlightKeyItems: true,
          summary: {
            enabled: true,
            subtitle: true,
            instructions: true,
            include: true,
            format: true
          },
          changeDrivers: {
            enabled: true,
            subtitle: true,
            instructions: true,
            include: true,
            focusOn: true,
            format: true,
            changeSensitivity: true
          },
          anomalyRadar: {
            enabled: true,
            subtitle: true,
            instructions: true,
            include: true,
            exclude: true,
            format: true,
            changeSensitivity: true
          },
          patternFinder: {
            enabled: true,
            subtitle: true,
            instructions: true,
            include: true,
            format: true,
            changeSensitivity: true
          },
          timelineOfEvents: {
            enabled: true,
            subtitle: true,
            instructions: true,
            include: true,
            format: true
          },
          proposeActions: {
            enabled: true,
            subtitle: true,
            instructions: true,
            actionType: true,
            format: true
          }
        }
      },
    },
  };
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function updateAiInsight(args) {
  const query = {
    mutation: {
      updateAiInsight: {
        __args: args,
        id: true,
        workspaceId: true,
        dataSource: true,
        title: true,
        dashboardId: true,
        chartsToInclude: true,
        mode: true,
        text: true,
        createdBy: true,
        createdAt: true,
        updatedAt: true,
        status: true,
        keyFinding: {
          enabled: true,
          instructions: true,
          include: true,
          textSize: true,
          highlightKeyItems: true
        },
        briefing: {
          enabled: true,
          title: true,
          highlightKeyItems: true,
          summary: {
            enabled: true,
            subtitle: true,
            instructions: true,
            include: true,
            format: true
          },
          changeDrivers: {
            enabled: true,
            subtitle: true,
            instructions: true,
            include: true,
            focusOn: true,
            format: true,
            changeSensitivity: true
          },
          anomalyRadar: {
            enabled: true,
            subtitle: true,
            instructions: true,
            include: true,
            exclude: true,
            format: true,
            changeSensitivity: true
          },
          patternFinder: {
            enabled: true,
            subtitle: true,
            instructions: true,
            include: true,
            format: true,
            changeSensitivity: true
          },
          timelineOfEvents: {
            enabled: true,
            subtitle: true,
            instructions: true,
            include: true,
            format: true
          },
          proposeActions: {
            enabled: true,
            subtitle: true,
            instructions: true,
            actionType: true,
            format: true
          }
        }
      },
    },
  };
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function deleteAiInsight(args) {
  const query = {
    mutation: {
      deleteAiInsight: {
        __args: args,
      },
    },
  };
  return handleQuery(query, AI_AGENTS_API_URL)
}

export function previewInsight(args) {
  const query = {
    query: {
      previewInsight: {
        __args: args
      },
    },
  };

  // starting a new request sequence implicitly resumes the queue
  previewInsightQueueCancelled = false;
  const controller = new AbortController();
  currentPreviewInsightController = controller;
  previewInsightControllers.add(controller);
  const request = enqueuePreviewInsightRequest(() =>
    handleQuery(query, AI_AGENTS_API_URL, { signal: controller.signal })
  );
  return request.finally(() => {
    previewInsightControllers.delete(controller);
  });
}